// Landscape Map — hand-rolled SVG per screen1-map.png (Story #4937 Stream
// 3b, amendment G: no d3/three.js/recharts in package.json, only cytoscape —
// this map is intentionally hand-rolled, not a new dependency). Thin render
// layer only: every scale/binning/outline/slider/hit-test calculation lives
// in src/lib/whitespace-map.ts, which is what vitest's coverage floors
// actually exercise (jsdom has no canvas, so this component itself is
// verified by the visual-diff gate against the mockup, not by vitest).
import { useMemo, useState, type MouseEvent } from 'react'
import { Sparkles, Star } from 'lucide-react'
import {
  layoutToPoints,
  buildScale,
  binPointsForDensity,
  computeRegionOutlines,
  dateRangeFromDates,
  filterPointsByDateRange,
  hitTestRegion,
  recordsPerYear,
  clampPublicationWindow,
  regionKeyFor,
  starPoints,
} from '../../lib/whitespace-map'
import type { WhitespaceMapDates, WhitespaceMapLayout, WhitespaceProbeLanding, WhitespaceRegion } from '../../types/whitespace'
import EmptyState from '../shared/EmptyState'
import PublicationWindowSlider from './PublicationWindowSlider'
import HelpPopover from '../help/HelpPopover'
import HowToReadThisCard from '../help/HowToReadThisCard'

const VIEWBOX_SIZE = 100

interface LandscapeMapProps {
  layout: WhitespaceMapLayout
  dates?: WhitespaceMapDates | null
  regions: WhitespaceRegion[]
  probeLandings?: WhitespaceProbeLanding[]
  onRegionClick?: (regionKey: string) => void
  /** design §5.4 — the map-placement probe feature, named "Probe this region
   * on the map" and carrying the clicked region's own context. Separate from
   * `onRegionClick` (which navigates away to the Explorer) — hovering a
   * region reveals a distinct small affordance for this action so neither
   * click shape is ambiguous about which it triggers. */
  onProbeRegion?: (region: WhitespaceRegion) => void
  onGenerateMap?: () => void
  isGenerating?: boolean
}

export default function LandscapeMap({
  layout,
  dates,
  regions,
  probeLandings = [],
  onRegionClick,
  onProbeRegion,
  onGenerateMap,
  isGenerating,
}: LandscapeMapProps) {
  const [hoveredRegion, setHoveredRegion] = useState<string | null>(null)
  const dateBounds = useMemo(() => dateRangeFromDates(dates), [dates])
  const [dateRange, setDateRange] = useState<[number, number] | null>(null)
  const activeRange = dateRange ?? dateBounds

  const allPoints = useMemo(() => layoutToPoints(layout, dates), [layout, dates])
  const scale = useMemo(() => buildScale(allPoints, VIEWBOX_SIZE), [allPoints])
  const visiblePoints = useMemo(
    () => (activeRange ? filterPointsByDateRange(allPoints, activeRange) : allPoints),
    [allPoints, activeRange],
  )
  const neighbourhoodPoints = visiblePoints.filter((p) => p.kind === 'neighbourhood')
  const projectPoints = visiblePoints.filter((p) => p.kind === 'project')
  const densityBins = useMemo(() => binPointsForDensity(neighbourhoodPoints, 14), [neighbourhoodPoints])
  const maxBinCount = Math.max(1, ...densityBins.map((b) => b.count))
  const outlines = useMemo(() => computeRegionOutlines(regions, layout), [regions, layout])
  const regionByKey = useMemo(() => new Map(regions.map((r) => [regionKeyFor(r), r])), [regions])
  // design §5.7 — the histogram is built over EVERY dated neighbourhood
  // point in the layout, never just the ones the current window already
  // shows (a histogram that only ever displayed the window it's filtering
  // couldn't show the viewer what they're excluding).
  const histogram = useMemo(() => recordsPerYear(allPoints, dateBounds), [allPoints, dateBounds])

  if (Object.keys(layout).length === 0) {
    return (
      <div data-tour="landscape-map">
        <EmptyState
          icon={<Sparkles className="w-7 h-7" />}
          title="No landscape map yet"
          description="Build a 2-D map of your idea against the corpus — sourced from the same scored regions as the Opportunity Explorer."
          action={onGenerateMap ? { label: isGenerating ? 'Building…' : 'Build map', onClick: onGenerateMap } : undefined}
        />
      </div>
    )
  }

  const handleCanvasClick = (evt: MouseEvent<SVGSVGElement>) => {
    if (!onRegionClick) return
    const svg = evt.currentTarget
    const rect = svg.getBoundingClientRect()
    const sx = ((evt.clientX - rect.left) / rect.width) * VIEWBOX_SIZE
    const sy = ((evt.clientY - rect.top) / rect.height) * VIEWBOX_SIZE
    const [dx, dy] = scale.toData(sx, sy)
    const key = hitTestRegion([dx, dy], outlines)
    if (key) onRegionClick(key)
  }

  return (
    <div data-testid="landscape-map" data-tour="landscape-map">
      <HowToReadThisCard
        visualisationId="whitespace-map"
        title="The Landscape Map"
        body="The amber star is your idea, small dots are nearby patents and papers, and dashed amber circles are the same scored regions as the Opportunity Explorer, placed in a flattened 2-D projection."
        example="Example: a dashed circle with no dots inside it and a high score beside it is a genuinely open region worth probing — hover it for the purple “P” probe marker."
      />
      <div className="flex items-center gap-3 mb-2 flex-wrap text-[10.5px] text-gray-500">
        <span className="flex items-center gap-1"><Star className="w-3 h-3 text-amber-400 fill-amber-400" /> Your idea</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-sky-400 inline-block" /> Patents &amp; articles</span>
        <span className="flex items-center gap-1">
          <span className="w-2.5 h-2.5 rounded-full border border-dashed border-amber-400 inline-block" /> White space
        </span>
        {probeLandings.length > 0 && (
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-purple-500 inline-block" /> Probes
          </span>
        )}
        <HelpPopover topic="ws-map" size={13} label="Help: how to read the landscape map" />
      </div>

      <svg
        data-testid="landscape-map-svg"
        viewBox={`0 0 ${VIEWBOX_SIZE} ${VIEWBOX_SIZE}`}
        className="w-full aspect-square bg-white rounded-xl border border-sky-100 cursor-pointer"
        onClick={handleCanvasClick}
      >
        {/* Corpus density underlay — BINS, never a raw point dump. */}
        {densityBins.map((bin, i) => {
          const [cx, cy] = scale.toScreen(bin.cx, bin.cy)
          const intensity = bin.count / maxBinCount
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={2 + intensity * 6}
              fill="#0EA5E9"
              opacity={0.08 + intensity * 0.3}
            />
          )
        })}

        {/* White-space region outlines — dashed, per screen1's ranked regions. */}
        {outlines.map((o) => {
          const [cx, cy] = scale.toScreen(o.cx, o.cy)
          const r = o.radius / scale.dataPerScreenUnit
          const isHovered = hoveredRegion === o.regionKey
          return (
            <g key={o.regionKey}>
              <circle
                data-testid="region-outline"
                cx={cx}
                cy={cy}
                r={r}
                fill={isHovered ? 'rgba(251,191,36,0.15)' : 'rgba(251,191,36,0.06)'}
                stroke="#FBBF24"
                strokeWidth={isHovered ? 1 : 0.6}
                strokeDasharray="2,1.5"
                onMouseEnter={() => setHoveredRegion(o.regionKey)}
                onMouseLeave={() => setHoveredRegion((k) => (k === o.regionKey ? null : k))}
                onClick={(e) => {
                  e.stopPropagation()
                  onRegionClick?.(o.regionKey)
                }}
              />
              <text x={cx} y={cy - r - 1.5} fontSize={3} textAnchor="middle" fill="#92400E" fontWeight={700}>
                score {Math.round(o.wos_score)}
              </text>
              {isHovered && onProbeRegion && (
                <g
                  data-testid={`probe-region-${o.regionKey}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    const region = regionByKey.get(o.regionKey)
                    if (region) onProbeRegion(region)
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <circle cx={cx} cy={cy + r + 3} r={3.2} fill="#8B5CF6" />
                  <text x={cx} y={cy + r + 4} fontSize={3.2} textAnchor="middle" fill="#ffffff" fontWeight={700}>
                    P
                  </text>
                  <title>Probe this region on the map</title>
                </g>
              )}
            </g>
          )
        })}

        {/* Neighbourhood points actually visible under the current time filter. */}
        {neighbourhoodPoints.map((p) => {
          const [cx, cy] = scale.toScreen(p.x, p.y)
          return <circle key={p.id} cx={cx} cy={cy} r={0.7} fill="#0369A1" opacity={0.55} />
        })}

        {/* Probe landings — a distinct marker so a user can compare a
            just-generated direction against the ranked regions. */}
        {probeLandings
          .filter((p): p is WhitespaceProbeLanding & { position: [number, number] } => !!p.position)
          .map((p, i) => {
            const [cx, cy] = scale.toScreen(p.position[0], p.position[1])
            return (
              <circle key={`probe-${i}`} data-testid="probe-marker" cx={cx} cy={cy} r={1.4} fill="#8B5CF6">
                <title>{p.text}</title>
              </circle>
            )
          })}

        {/* Project markers — the inventor's own idea, always on top. Rendered as
            a star to match the legend and help copy ("The amber star is your
            idea"); it was previously a plain dot, indistinguishable from the
            corpus markers (UX review Finding 5). */}
        {projectPoints.map((p) => {
          const [cx, cy] = scale.toScreen(p.x, p.y)
          return (
            <g key={p.id}>
              <polygon
                points={starPoints(cx, cy, 3.2, 1.3)}
                fill="#FBBF24"
                stroke="#92400E"
                strokeWidth={0.3}
                strokeLinejoin="round"
                aria-label="Your idea"
              />
            </g>
          )
        })}
      </svg>

      {dateBounds && (
        <PublicationWindowSlider
          bounds={dateBounds}
          value={activeRange ?? dateBounds}
          histogram={histogram}
          onChange={(next) => setDateRange(clampPublicationWindow(next, dateBounds))}
        />
      )}
    </div>
  )
}
